import { createContext, useContext, useState, ReactNode } from "react";

export type Region = "europe" | "international";
export type Currency = "EUR" | "USD" | "GBP" | "AED";

interface CurrencyConfig {
  symbol: string;
  rate: number;
  locale: string;
}

const currencyConfigs: Record<Currency, CurrencyConfig> = {
  EUR: { symbol: "€", rate: 1, locale: "de-DE" },
  USD: { symbol: "$", rate: 1.08, locale: "en-US" },
  GBP: { symbol: "£", rate: 0.85, locale: "en-GB" },
  AED: { symbol: "AED", rate: 3.97, locale: "ar-AE" },
};

interface RegionContextType {
  region: Region;
  setRegion: (region: Region) => void;
  formatArea: (sqm: string) => string;
  areaUnit: string;
  currency: Currency;
  setCurrency: (currency: Currency) => void;
  formatPrice: (euroPrice: string) => string;
  currencySymbol: string;
}

const RegionContext = createContext<RegionContextType | undefined>(undefined);

const getStoredValue = <T,>(key: string, defaultValue: T): T => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      const saved = localStorage.getItem(key);
      return saved ? (saved as T) : defaultValue;
    }
  } catch {
    // localStorage not available
  }
  return defaultValue;
};

const setStoredValue = (key: string, value: string): void => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      localStorage.setItem(key, value);
    }
  } catch {
    // localStorage not available
  }
};

export const RegionProvider = ({ children }: { children: ReactNode }) => {
  const [region, setRegionState] = useState<Region>(() => 
    getStoredValue<Region>("preferred-region", "europe")
  );

  const [currency, setCurrencyState] = useState<Currency>(() => 
    getStoredValue<Currency>("preferred-currency", "EUR")
  );

  const setRegion = (newRegion: Region) => {
    setRegionState(newRegion);
    setStoredValue("preferred-region", newRegion);
  };

  const setCurrency = (newCurrency: Currency) => {
    setCurrencyState(newCurrency);
    setStoredValue("preferred-currency", newCurrency);
  };

  const formatArea = (sqm: string): string => {
    if (region === "europe") {
      return sqm;
    }
    const sqmValue = parseFloat(sqm.replace(/,/g, ""));
    const sqftValue = Math.round(sqmValue * 10.764);
    return sqftValue.toLocaleString();
  };

  const formatPrice = (priceString: string): string => {
    // Strip all non-numeric characters to get the raw number
    const numericValue = parseFloat(priceString.replace(/[^0-9.]/g, ""));
    if (isNaN(numericValue)) return priceString;
    
    // All prices are stored in their local currency; we treat them as EUR-equivalent for conversion
    const config = currencyConfigs[currency];
    const convertedValue = numericValue * config.rate;
    
    if (currency === "EUR") {
      return `€${convertedValue.toLocaleString("de-DE", { maximumFractionDigits: 0 })}`;
    } else if (currency === "USD") {
      return `$${convertedValue.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
    } else if (currency === "GBP") {
      return `£${convertedValue.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
    } else {
      return `AED ${convertedValue.toLocaleString("en-AE", { maximumFractionDigits: 0 })}`;
    }
  };

  const areaUnit = region === "europe" ? "m²" : "sq ft";
  const currencySymbol = currencyConfigs[currency].symbol;

  return (
    <RegionContext.Provider value={{ 
      region, 
      setRegion, 
      formatArea, 
      areaUnit,
      currency,
      setCurrency,
      formatPrice,
      currencySymbol
    }}>
      {children}
    </RegionContext.Provider>
  );
};

export const useRegion = () => {
  const context = useContext(RegionContext);
  if (context === undefined) {
    throw new Error("useRegion must be used within a RegionProvider");
  }
  return context;
};
